import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  aiPrSummaryVsStandupPath,
  digestExamplesPath,
  githubDigestPath,
  publicChangelogOwnerPath,
  publicChangelogRepositoryPath,
  replaceStandupsPath,
  rootPath,
  signInPath,
} from '@/routes'
import { Link } from '@inertiajs/react'
import { ArrowRight, HelpCircleIcon } from 'lucide-react'

const SIGN_IN_CAMPAIGN = 'utm_source=what-is-a-github-digest&utm_campaign=definition'

const linkClass = 'text-foreground decoration-golden-500 underline underline-offset-4'

type Faq = {
  question: string
  answer: string
}

function TextLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link href={href} className={linkClass}>
      {children}
    </Link>
  )
}

export function WhatIsAGithubDigestArticle({ headline, faqs }: { headline: string; faqs: Faq[] }) {
  return (
    <div className="mx-auto max-w-4xl space-y-12 md:space-y-16">
      <header className="space-y-4 text-center">
        <p className="text-golden-500 text-sm font-semibold tracking-wider uppercase">GitHub digest</p>
        <h1 className="text-foreground text-4xl font-bold tracking-tight text-balance sm:text-5xl md:text-6xl">
          {headline}
        </h1>
        <p className="text-muted-foreground mx-auto max-w-2xl text-lg leading-relaxed sm:text-xl">
          What is a GitHub digest? It is a summary of the commits that landed in the repositories a team selects.{' '}
          <TextLink href={rootPath()}>Git Digest</TextLink> sends that summary to Slack and email, daily or weekly, so
          engineering managers can see what shipped.
        </p>
      </header>

      <Card className="border-golden-500/20 from-golden-500/5 via-background to-muted/30 bg-gradient-to-br p-6 text-center sm:p-8">
        <p className="text-muted-foreground text-sm leading-relaxed sm:text-base">
          Git Digest is a team product. You pick the GitHub repositories, and the digest goes to the people who need
          it. A tool that turns a whole repository into text for a language model does a different job.
        </p>
      </Card>

      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">What a GitHub digest is</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            Sign in with GitHub and choose the repositories that belong in the digest. Only those repos are included. A
            daily digest covers the previous 24 hours. A weekly digest covers the past week and is prepared on Mondays.
          </p>
          <p>
            Email goes to workspace members who have the email digest turned on, at the email hour you set. Slack goes
            to the channel stored when you connect Slack, at the Slack hour you set. Digests are prepared for
            workspaces with an active subscription. The homepage Unlimited plan is $29 per month, or $25 per month on
            yearly billing, for the team rather than per seat. A lifetime option is $1,000 once.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">What the digest contains</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            Each change is a short summary, the author, and a link back to GitHub. When the change belongs to a pull
            request, the link uses that pull request number. Otherwise it uses a short commit id.
          </p>
          <p>
            Slack groups the lines under Feature, Bugfix, Improvement, Chore, and Refactoring. The header is the date
            for a daily digest, or the date range for a weekly one. Email shows the first five changes in each
            repository, with lines added and removed. If the window is empty, Slack says there were no code changes.
          </p>
          <p>
            The <TextLink href={replaceStandupsPath()}>replace standups</TextLink> guide walks through using that
            delivery in place of a status meeting. The <TextLink href={digestExamplesPath()}>digest examples</TextLink>{' '}
            page shows three real lines from public changelogs, with the Slack tag, the email shape, and an
            illustrative standup line for each.
          </p>
          <p>
            An AI summary of a single pull request is a different artifact. It describes that pull request for
            reviewers. The <TextLink href={aiPrSummaryVsStandupPath()}>AI PR summary vs standup</TextLink> page is
            where that summary stops and a team digest starts.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">What a GitHub digest link does</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            What does a GitHub digest link do? In Git Digest, the public link is a shareable changelog for one
            repository. Turn it on in the repository settings and anyone can open the page without signing in. They
            cannot see the rest of the workspace.
          </p>
          <p>
            The page lives at a URL like /changelogs/owner/repo. Repositories with the public link on are listed for an
            owner, for example{' '}
            <TextLink href={publicChangelogOwnerPath('sunergos-ro')}>/changelogs/sunergos-ro</TextLink>. Open one
            repository, such as{' '}
            <TextLink href={publicChangelogRepositoryPath('sunergos-ro', 'og-pilot')}>sunergos-ro/og-pilot</TextLink>,
            and you get the same kind of summary the digest is built from: one sentence per change, the author, and
            lines added and removed.
          </p>
          <p>
            The public page is the changelog you can share. Slack and email are the scheduled copy for the team. Inside
            those messages, each change links to the commit or pull request on GitHub. Email also includes a link to
            that repository&apos;s changes inside Git Digest, for people who are already in the workspace.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">Git Digest and Gitingest</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            Some results for a github digest describe Gitingest, which turns a repository into text for a language
            model. Git Digest summarizes what landed in GitHub and posts that digest to Slack and email for the team.
            The <TextLink href={githubDigestPath()}>GitHub digest comparison</TextLink> is the side-by-side of those
            two products.
          </p>
        </div>
      </section>

      <section className="space-y-6">
        <div>
          <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">GitHub digest FAQ</h2>
          <p className="text-muted-foreground mt-3 text-base">
            Short answers for what a GitHub digest is, and for what the public digest link opens.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {faqs.map((faq) => (
            <Card key={faq.question} className="h-full">
              <CardContent className="pt-6">
                <h3 className="text-foreground mb-3 flex items-start gap-x-3 text-lg">
                  <span className="flex h-[1lh] items-center">
                    <HelpCircleIcon className="text-golden-500 size-4 shrink-0" />
                  </span>
                  {faq.question}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{faq.answer}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <Card className="border-orange-200/50 bg-gradient-to-br from-orange-50 to-orange-100/30 p-8 text-center dark:border-orange-800/30 dark:from-orange-500/10 dark:to-orange-500/5">
        <h2 className="mb-3 text-2xl text-gray-900 dark:text-white">Send the team a GitHub digest</h2>
        <p className="text-muted-foreground mx-auto mb-6 max-w-2xl text-sm">
          Connect GitHub, pick the repositories, and get daily or weekly commit summaries in Slack and email.
        </p>
        <Button
          asChild
          size="lg"
          className="bg-golden-500 hover:bg-golden-600 font-semibold text-black shadow-lg hover:shadow-xl"
        >
          <Link href={`${signInPath()}?${SIGN_IN_CAMPAIGN}`}>
            Start with GitHub
            <ArrowRight className="ml-2 size-4 shrink-0" />
          </Link>
        </Button>
      </Card>
    </div>
  )
}
